/**
 * Account-deletion confirmation modal.
 *
 * Gates (irreversible operation):
 *   1. Current password — BetterAuth's deleteUser endpoint verifies it
 *      server-side. (No server-side TOTP check is available; we don't
 *      add a client-side TOTP gate because it's theatre — an attacker
 *      with session+password can call /api/auth/delete-user directly
 *      and bypass any UI prompt. 2FA defends sign-in; password defends
 *      destructive ops on a live session.)
 *   2. Type the literal word "DELETE" to confirm — fat-finger guard for
 *      an irreversible action. Pattern matches GitHub / AWS / Stripe.
 *
 * On success: BetterAuth deletes the user, the session cookie is cleared
 * server-side, and the next render of useSession() returns null. The route
 * guard then bounces the (now-anonymous) browser to /login. We don't push
 * to /login manually — letting the auth machinery drive the redirect keeps
 * the flow consistent with all other "session ended" paths.
 */
import { useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { authClient } from '@/shared/auth/authClient';
import { useAuth } from '@/shared/auth/AuthProvider';
import { Button, Dialog, Form, FormTextField } from '@/interfaces/learner/ui';
import LoadingIcon from '@/foundation/ui/LoadingIcon';

// ─── Schema ─────────────────────────────────────────────────────────────────

const CONFIRM_WORD = 'DELETE';

const formSchema = z
  .object({
    password: z.string().min(1, 'Password is required'),
    // The exact-string check happens at submit time so we can show "type DELETE
    // to confirm" as a placeholder rather than a constant validation error
    // before the user's even typed anything.
    confirmation: z.string(),
  })
  .refine((d) => d.confirmation === CONFIRM_WORD, {
    message: `Type ${CONFIRM_WORD} to confirm`,
    path: ['confirmation'],
  });
type FormValues = z.infer<typeof formSchema>;

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function DeleteAccountModal({ isOpen, onClose }: DeleteAccountModalProps) {
  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Delete account" size="md">
      {/* Re-mount the body on each open so transient state (error
          messages, partial typing) starts clean. */}
      {isOpen && <ModalBody onClose={onClose} />}
    </Dialog>
  );
}

function ModalBody({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: '', confirmation: '' },
    mode: 'onBlur',
  });

  const [submitting, setSubmitting] = useState(false);

  const handle: SubmitHandler<FormValues> = async ({ password }) => {
    setSubmitting(true);
    try {
      const { error } = await authClient.deleteUser({ password });
      if (error) {
        toast.error(error.message ?? 'Could not delete your account');
        return;
      }

      toast.success('Account deleted.');
      // Don't navigate manually — the cookie is cleared, useSession will
      // flip to null on the next poll, and RequireAuth will redirect to
      // /login. Just close the modal so the brief between-states UI is
      // clean.
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={form.handleSubmit(handle)} className="space-y-4 pt-1">
      <div
        className="flex items-start gap-3 p-3 rounded-lg"
        style={{
          background: 'var(--color-error-subtle)',
          border: '1px solid var(--color-error)',
        }}
      >
        <AlertTriangle
          size={18}
          strokeWidth={2}
          style={{ color: 'var(--color-error-text)', flexShrink: 0, marginTop: 2 }}
        />
        <div className="text-sm" style={{ color: 'var(--color-error-text)' }}>
          <p className="font-semibold mb-1">This cannot be undone.</p>
          <p>
            Your account ({user?.email ?? 'your account'}), all associated learning data, generated
            lessons, and progress will be permanently removed.
          </p>
        </div>
      </div>

      <FormTextField
        control={form.control}
        name="password"
        label="Current password"
        type="password"
        autoComplete="current-password"
        isRequired
        autoFocus
      />

      <FormTextField
        control={form.control}
        name="confirmation"
        label={`Type ${CONFIRM_WORD} to confirm`}
        placeholder={CONFIRM_WORD}
        autoComplete="off"
      />

      <div className="flex gap-2 justify-end pt-2">
        <Button variant="ghost" onPress={onClose} isDisabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" variant="danger" isDisabled={submitting}>
          {submitting ? (
            <span className="flex items-center gap-2">
              <LoadingIcon size="sm" />
              Deleting...
            </span>
          ) : (
            'Permanently delete account'
          )}
        </Button>
      </div>
    </Form>
  );
}
